import { FC, ReactNode } from 'react';
import { Box, Text, Flex, Image } from '@chakra-ui/react';

interface VideoAdCatalogItemProps {
  catalogId: string;
  name: string;
  imageUrl?: string;
  type?: string;
  onSelect: (catalogId: string) => void;
}

export const VideoAdCatalogItem: FC<VideoAdCatalogItemProps> = ({
  catalogId,
  name,
  imageUrl,
  type,
  onSelect,
}) => {
  const isCreateNew = catalogId === '__create_new__';

  return (
    <Box
      as="button"
      display="flex"
      alignItems="center"
      gap={3}
      w="100%"
      p={3}
      border="1.5px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      cursor="pointer"
      transition="all 0.15s"
      onClick={() => onSelect(catalogId)}
      _hover={{
        borderColor: 'orange.base',
        bg: 'orange.50',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      }}
    >
      {isCreateNew ? (
        // "Create new" button
        <>
          <Flex
            w="48px"
            h="48px"
            borderRadius="lg"
            bg="gray.100"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M5 12H19" stroke="#F04D24" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </Flex>
          <Text fontSize="sm" fontWeight="semibold" color="orange.600" textAlign="left">
            Create new product/service
          </Text>
        </>
      ) : (
        // Catalog item
        <>
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={name}
              w="48px"
              h="48px"
              borderRadius="lg"
              objectFit="cover"
              flexShrink={0}
            />
          ) : (
            <Flex
              w="48px"
              h="48px"
              borderRadius="lg"
              bg="gray.100"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 8L12 2L3 8V16L12 22L21 16V8Z" stroke="#A0AEC0" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M12 22V12" stroke="#A0AEC0" strokeWidth="1.5"/>
                <path d="M21 8L12 12L3 8" stroke="#A0AEC0" strokeWidth="1.5"/>
              </svg>
            </Flex>
          )}
          <Box flex={1} textAlign="left" minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
              {name}
            </Text>
            {type && type !== 'new' && (
              <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                {type}
              </Text>
            )}
          </Box>
          {imageUrl && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#CBD5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </>
      )}
    </Box>
  );
};

interface VideoAdCatalogListProps {
  children?: ReactNode;
  onSelect: (catalogId: string) => void;
}

const VideoAdCatalogList: FC<VideoAdCatalogListProps> = ({ children, onSelect }) => {
  return (
    <Flex direction="column" gap={2} w="100%" maxW="360px" my={2}>
      {children}
    </Flex>
  );
};

export default VideoAdCatalogList;
